import { FiArrowLeft } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { FaUsers, FaRegClock, FaDollarSign } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import caseStudies from './CaseData'

const CaseStudyDetail = () => {
  const { slug } = useParams();
const study = caseStudies.find((s) => s.slug === slug);

  if (!study) {
    return <div className="bg-gray-900 text-white min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl mb-4">Case study not found</h2>
        <Link to="/case-studies" className="text-blue-400 hover:text-blue-300">
          Back to case studies
        </Link>
      </div>
    </div>;
  }

  return (
    <>
      <Helmet>
        <title>{study.title} | Case Study</title>
        <meta name="description" content={`How we helped ${study.client} achieve remarkable results`} />
      </Helmet>

      <main className="bg-gray-900 text-gray-100 min-h-screen">
        {/* Back Button */}
        <div className="container mx-auto px-6 pt-10">
          <Link 
            to="/case-studies" 
            className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-8"
          >
            <FiArrowLeft className="mr-2" /> Back to Case Studies
          </Link>
        </div>

        {/* Hero Section */}
        <section className="container mx-auto px-6 pb-20">
          <div className="mb-8">
            <span className="inline-block px-3 py-1 bg-blue-900/30 text-blue-400 rounded-full text-sm mb-4">
              {study.industry}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{study.title}</h1>
            <div className="flex flex-wrap gap-4 text-gray-400">
              <div className="flex items-center">
                <FaUsers className="mr-2" /> Client: {study.client}
              </div>
              <div className="flex items-center">
                <FaRegClock className="mr-2" /> Duration: {study.duration}
              </div>
              <div className="flex items-center">
                <FaDollarSign className="mr-2" /> Budget: {study.budget}
              </div>
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden shadow-2xl mb-16">
            <img
              src={study.coverImage}
              alt={study.title}
              className="w-full h-auto"
            />
          </div>

          {/* Challenge & Solution */}
          <div className="grid md:grid-cols-2 gap-12 mb-20">
            <div>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">The Challenge</h2>
              <p className="text-gray-400 leading-relaxed">{study.challenge}</p>
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">Our Solution</h2>
              <p className="text-gray-400 leading-relaxed">{study.solution}</p>
            </div>
          </div>

          {/* Results */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-12 text-center">Key Results</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {study.results.map((result, index) => (
                <div key={index} className="bg-gray-800 p-6 rounded-xl text-center">
                  <div className="text-blue-400 text-3xl mb-3 flex justify-center">
                    {result.icon}
                  </div>
                  <div className="text-4xl font-bold mb-2">{result.metric}</div>
                  <p className="text-gray-400">{result.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Testimonial */}
          <div className="bg-gray-800 rounded-2xl p-8 md:p-12 mb-20 border border-gray-700">
            <div className="flex items-center mb-6">
              <img
                src={study.testimonial.avatar}
                alt={study.testimonial.author}
                className="w-16 h-16 rounded-full mr-4"
              />
              <div>
                <h4 className="font-bold">{study.testimonial.author}</h4>
                <p className="text-gray-400">{study.testimonial.role}</p>
              </div>
            </div>
            <blockquote className="text-xl italic text-gray-300 mb-6">
              "{study.testimonial.text}"
            </blockquote>
          </div>

          {/* Gallery */}
          {study.gallery && (
            <div className="mb-20">
              <h2 className="text-3xl font-bold mb-12 text-center">Project Gallery</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {study.gallery.map((image, index) => (
                  <div key={index} className="rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src={image}
                      alt={`${study.title} screenshot ${index + 1}`}
                      className="w-full h-auto"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to transform your business?</h2>
            <Link 
              to="/contact" 
              className="inline-block px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-full font-bold text-lg transition-colors"
            >
              Get in Touch
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default CaseStudyDetail;